import { useState } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Button,
    Box,
    Typography,
} from '@mui/material';
import { MoreVert as MoreIcon } from '@mui/icons-material';

type Item = {
    show?: boolean;
    divider?: boolean;
    caption: string;
    icon?: React.ReactElement;
    onClick?: () => void;
    isHeader?: boolean;
};

type SttMenuProps = {
    type?: 'icon' | 'button';
    caption?: string;
    icon?: React.ReactElement;
    items: Item[];
};

export function SttMenu({
    type = 'icon',
    caption,
    icon,
    items,
    ...rest
}: SttMenuProps) {
    const [buttonMenuElm, setButtonMenuElm] =
        useState<HTMLButtonElement | null>(null);

    return (
        <>
            {type === 'icon' && (
                <IconButton
                    color="inherit"
                    onClick={(event: any) =>
                        setButtonMenuElm(event.currentTarget)
                    }
                    sx={{
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                            transform: 'scale(1.1)',
                        },
                    }}
                    {...rest}
                >
                    {icon || <MoreIcon />}
                </IconButton>
            )}

            {type === 'button' && (
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={icon}
                    onClick={(ev: any) => setButtonMenuElm(ev.currentTarget)}
                    {...rest}
                >
                    {caption}
                </Button>
            )}

            <Menu
                anchorEl={buttonMenuElm}
                open={buttonMenuElm !== null}
                onClose={() => setButtonMenuElm(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.12))',
                            borderRadius: '12px',
                            mt: 1.5,
                            minWidth: 200,
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 12,
                                height: 12,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    },
                }}
            >
                {items
                    .filter(({ show = true }: any) => show)
                    .map((item, index, filteredItems) => (
                        <div key={item.caption}>
                            {item.divider && (
                                <Divider sx={{ my: 0.5, mx: 1.5 }} />
                            )}
                            {item.isHeader ? (
                                <Box
                                    sx={{
                                        py: 1.5,
                                        px: 2,
                                        mx: 0.75,
                                        mt: index === 0 ? 0.75 : 0.25,
                                        mb: 0.25,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '0.95rem',
                                            fontWeight: 600,
                                            color: '#333',
                                        }}
                                    >
                                        {item.caption}
                                    </Typography>
                                </Box>
                            ) : (
                                <MenuItem
                                    onClick={() => {
                                        item.onClick?.();
                                        setButtonMenuElm(null);
                                    }}
                                    sx={{
                                        py: 1.25,
                                        px: 2,
                                        mx: 0.75,
                                        my: 0.25,
                                        borderRadius: '8px',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor:
                                                'rgba(186, 242, 0, 0.1)',
                                            transform: 'translateX(4px)',
                                        },
                                        ...(index === 0 && { mt: 0.75 }),
                                        ...(index ===
                                            filteredItems.length - 1 && {
                                            mb: 0.75,
                                        }),
                                    }}
                                >
                                    {item.icon && (
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 36,
                                                color: '#666',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 20,
                                                },
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                    )}
                                    <ListItemText
                                        primary={item.caption}
                                        primaryTypographyProps={{
                                            fontSize: '0.9rem',
                                            fontWeight: 500,
                                            color: '#333',
                                        }}
                                    />
                                </MenuItem>
                            )}
                        </div>
                    ))}
            </Menu>
        </>
    );
}
